import { useEffect, useState } from "react";

/**
 * Viewport breakpoints used to switch between the mobile PhoneShell
 * and DesktopShell. Per the design system desktop README, 1280px is
 * the floor for the desktop layout; below that the mobile kit takes
 * over. We use 1024px as the switch point — gives tablets a desktop
 * layout, gives small laptops the rich layout right away.
 */
export const DESKTOP_BREAKPOINT_PX = 1024;

export type Viewport = "mobile" | "desktop";

/** SSR-safe initial value; on server we default to mobile. */
function initialViewport(): Viewport {
  if (typeof window === "undefined") return "mobile";
  return window.innerWidth >= DESKTOP_BREAKPOINT_PX ? "desktop" : "mobile";
}

/**
 * React hook returning the current viewport tier. Re-evaluates on
 * window resize (debounced via rAF) so the shell switches live as
 * the user drags the window across the breakpoint.
 */
export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(initialViewport);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let rafId: number | null = null;
    const onResize = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        const next: Viewport =
          window.innerWidth >= DESKTOP_BREAKPOINT_PX ? "desktop" : "mobile";
        setViewport((prev) => (prev === next ? prev : next));
        rafId = null;
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return viewport;
}
